<script>
  import axios from "axios"
  import route from "@helpers/route"
  import { toHuman } from "@helpers/formatters/currency"
  import { addedToCartDialog, merchant, totalCartItems } from "@store"
  import Link from "@components/Link.svelte"
  import ProtectedImage from "@components/ProtectedImage.svelte"
  import SubmitButton from "@components/SubmitButton.svelte"
  import Analytics from "@services/Analytics"
  import AffirmPromotionalMessaging, { AffirmPageType } from "@components/AffirmPromotionalMessaging.svelte"
  import ProductAvailability from "@components/ProductAvailability.svelte"
  import { getUnitPriceSuffix } from "@helpers/models/product"
  export let product
  export let collection

  const url = route("shop.collections.products.show", [collection, collection.slug, product, product.slug])

  let processing = false

  $: isSampleRequest = product.selection.is_sample_request && product.is_sampleable
  $: unitPriceInCents = isSampleRequest ? product.sample_price_in_cents : product.retail_price_with_shipping_in_cents
  $: unitPriceSuffix = isSampleRequest ? "/ea" : getUnitPriceSuffix(product)

  async function submit() {
    processing = true

    const response = await axios.post(route("shop.checkout.cart-items.store"), {
      collection_id: collection.id,
      product_id: product.id,
    })
    Analytics.track("User added product to cart", {
      collectionId: collection.id,
      product: {
        productId: product.id,
        sku: product.sku,
        productName: product.name,
        price: unitPriceInCents * 0.01,
        imageUrl: product.featured_image_url,
      },
    })

    processing = false
    $totalCartItems++
    addedToCartDialog.open(response.data.item)
  }
</script>

<article class="c-product-card [ relative flex flex-col ] [ group ]">
  <div class="c-product-card__photo [ border relative aspect-ratio-square ]">
    <Link href={url} title="View {product.name}" class="[ h-full w-full block group ]">
      <ProtectedImage
        aspect="square"
        src={product.featured_image_url}
        alt={product.name}
        class="
          c-product-card__image c-product-card__image--featured
          [ w-full transition-opacity duration-100 ]
          {product.hover_image_url ? '[ group-hover:opacity-0 group-focus:opacity-0 ]' : ''}
        "
      />

      {#if product.hover_image_url}
        <div class="[ absolute inset-0 ]">
          <ProtectedImage
            aspect="square"
            src={product.hover_image_url}
            alt={product.name}
            class="
            c-product-card__image c-product-card__image--hover
            [ w-full transition-opacity duration-100 ]
            [ opacity-0 group-hover:opacity-100 group-focus:opacity-100 ]
          "
          />
        </div>
      {/if}
    </Link>

    <div
      class="
        [ hidden lg:flex ]
        [ absolute inset-0 items-center justify-center ]
        [ transition-colors duration-75 ]
        [ bg-black bg-opacity-0 group-hover:bg-opacity-50 focus-within:bg-opacity-50 ]
      "
    >
      <Link href={url} class="[ absolute inset-0 ]" tabindex="-1" />

      <form on:submit|preventDefault={submit}>
        <SubmitButton
          class="
            c-product-card__add-to-cart-button
            [ font-semibold tracking-tight text-sm relative z-content ]
            [ transition-opacity duration-75 opacity-0 ]
            [ group-hover:opacity-100 focus:opacity-100 ]
          "
          {processing}
          tabindex="0"
        >
          <span class="sr-only">Add {product.name} to your cart</span>
          <span aria-hidden="true">Add to Cart</span>
        </SubmitButton>
      </form>
    </div>
  </div>

  <div class="c-product-card__details [ mt-4 flex flex-col grow justify-between ]">
    <div class="flex flex-col grow">
      {#if collection.settings.shows_vendor_name}
        <p class="c-product-card__vendor [ font-functional text-neutral-700 ]">
          {product.vendor.name}
        </p>
      {/if}

      <Link type="stroke" href={url}>
        <h2 class="c-product-card__name [ text-xl leading-tight line-clamp-2 ]">
          {product.name}
        </h2>
      </Link>

      <div class="mt-auto pt-2">
        <div class="c-product-card__price [ text-3xl font-semibold tracking-tighter ]">
          {toHuman(unitPriceInCents * 0.01)}{unitPriceSuffix}
        </div>

        {#if $merchant.show_affirm_product_promotional_messaging && !collection.settings.hide_affirm_product_promotional_messaging}
          <AffirmPromotionalMessaging
            class="font-sans text-sm"
            amountInCents={unitPriceInCents}
            pageType={AffirmPageType.Search}
          />
        {/if}
      </div>

      <div class="flex">
        <ProductAvailability class="mt-2" {product} {collection} sample={isSampleRequest} />
      </div>
    </div>

    <div class="[ block lg:hidden pt-4 mt-auto ]">
      <form on:submit|preventDefault={submit}>
        <SubmitButton {processing} class="c-product-card__add-to-card [ font-semibold w-full ]">
          <span class="sr-only">Add {product.name} to your cart</span>
          <span aria-hidden="true">Add to Cart</span>
        </SubmitButton>
      </form>
    </div>
  </div>
</article>
